import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { FullPageLoader } from "@/components/Common";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import VerifyEmail from "@/pages/VerifyEmail";
import ResetPassword from "@/pages/ResetPassword";
import NotFound from "@/pages/NotFound";
import ConsentBanner from "@/components/ConsentBanner";
import { consumePostLoginNext, resolveLanding } from "@/lib/auth";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import TaskWorkspace from "@/pages/TaskWorkspace";
import MyBusiness from "@/pages/MyBusiness";
import AIStudio from "@/pages/AIStudio";
import LaunchCenter from "@/pages/LaunchCenter";
import BuildStudio from "@/pages/BuildStudio";
import BlueprintVoiceInterview from "@/pages/BlueprintVoiceInterview";
import BuildSuccess from "@/pages/BuildSuccess";
import CareSuccess from "@/pages/CareSuccess";
import FounderBuildOrders from "@/pages/FounderBuildOrders";
import FounderTestimonials from "@/pages/FounderTestimonials";
import FounderDashboard from "@/pages/FounderDashboard";
import FounderStudents from "@/pages/FounderStudents";
import FounderAnalytics from "@/pages/FounderAnalytics";
import FounderAdmin from "@/pages/FounderAdmin";
import StudentDetail from "@/pages/StudentDetail";
import { Privacy, Terms, Help } from "@/pages/Legal";

function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sid });
        setUser(data.user);
        window.history.replaceState(null, "", window.location.pathname);
        const u = data.user;
        const next = consumePostLoginNext();
        if (u.role === "founder") navigate("/founder", { replace: true, state: { user: u } });
        else if (!u.onboarding_complete) navigate("/onboarding", { replace: true, state: { user: u } });
        else {
          const dest = await resolveLanding(u, next);
          navigate(dest, { replace: true, state: { user: u } });
        }
      } catch {
        navigate("/login?error=1", { replace: true });
      }
    })();
  }, []); // eslint-disable-line

  return <FullPageLoader label="Signing you in…" />;
}

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <FullPageLoader label="Loading…" />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === "founder" ? "/founder" : "/dashboard"} replace />;
  }
  if (role !== "founder" && user.role === "student" && !user.onboarding_complete && pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Login />} />
      <Route path="/verify" element={<VerifyEmail />} />
      <Route path="/reset" element={<ResetPassword />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/help" element={<Help />} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/dashboard" element={<Protected role="student"><Dashboard /></Protected>} />
      <Route path="/task/:slug" element={<Protected role="student"><TaskWorkspace /></Protected>} />
      <Route path="/business" element={<Protected role="student"><MyBusiness /></Protected>} />
      <Route path="/ai-studio" element={<Protected role="student"><AIStudio /></Protected>} />
      <Route path="/launch" element={<Protected role="student"><LaunchCenter /></Protected>} />
      <Route path="/build" element={<Protected role="student"><BuildStudio /></Protected>} />
      <Route path="/build/voice" element={<Protected role="student"><BlueprintVoiceInterview /></Protected>} />
      <Route path="/build/success" element={<Protected role="student"><BuildSuccess /></Protected>} />
      <Route path="/care/success" element={<Protected role="student"><CareSuccess /></Protected>} />
      <Route path="/build/cancel" element={<Protected role="student"><BuildStudio /></Protected>} />
      <Route path="/founder" element={<Protected role="founder"><FounderDashboard /></Protected>} />
      <Route path="/founder/students" element={<Protected role="founder"><FounderStudents /></Protected>} />
      <Route path="/founder/analytics" element={<Protected role="founder"><FounderAnalytics /></Protected>} />
      <Route path="/founder/admin" element={<Protected role="founder"><FounderAdmin /></Protected>} />
      <Route path="/founder/students/:id" element={<Protected role="founder"><StudentDetail /></Protected>} />
      <Route path="/founder/build-orders" element={<Protected role="founder"><FounderBuildOrders /></Protected>} />
      <Route path="/founder/testimonials" element={<Protected role="founder"><FounderTestimonials /></Protected>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <ConsentBanner />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
